import {
  capitalize,
  cloneDeep,
  isObjectLike,
} from 'lodash';
import * as breakdownConstants from '@/constants/products/breakdown';
import * as commonConstants from '@/constants/common';
import {
  nameRegex,
} from '@/helpers/common';
import {
  UtcDate,
  DATE_FORMAT,
} from '@/helpers/date';
import { getQuotePayload } from '@/payloadTemplates/pet';
import pet from '@/payloadTemplates/pet/pet';
import { validate } from 'vee-validate';

const constants = Object.assign(
  {},
  commonConstants,
  breakdownConstants,
);

export const initProposerRules = (options) => (step = {}) => ({
  petTypeOfCover: {
    typeof: 'number',
    required: true,
  },
  coverAmount: {
    typeof: 'number',
    required: true,
  },
  coverDuration: {
    typeof: 'number',
    required: true,
  },
  dateOfInsuranceStart: {
    date_format: DATE_FORMAT,
    after: {
      equal: true,
      compare: 'today',
    },
    before: {
      equal: true,
      compare: new UtcDate().addDays(constants.DATE_OF_INSURANCE_START_MAX_DAYS).format(),
    },
  },
  title: {
    in: {
      items: constants.TITLES,
    },
  },
  firstName: {
    name: [2, 50],
    regex: nameRegex,
  },
  surname: {
    name: [2, 50],
    regex: nameRegex,
  },
  dateOfBirth: {
    date_format: DATE_FORMAT,
    dateOfBirth: true,
  },
  email: {
    email: true,
    required: true,
  },
  postcode: {
    postcode: true,
  },
  houseNumberOrName: {
    typeof: 'string',
  },
  addressLine2: {
    typeof: 'string',
  },
  addressLine3: {
    typeof: 'string',
  },
  addressLine4: {
    typeof: 'string',
  },
  userKeepSignIn: {
    required: !options.config.hiddenQuestions['user-keep-me-sign-in'],
    typeof: 'boolean',
  },
});

export const initCoverRules = () => (step = {}) => {
  return (step.pets || []).map((pet) => {
    return {
      pet: {
        typeof: 'string',
        required: true,
      },
      petName: {
        typeof: 'string',
        required: true,
      },
      petGender: {
        typeof: 'string',
        required: true,
      },
      petDateOfBirth: {
        date_format: DATE_FORMAT,
        dateOfBirth: true,
        petCheckMinAge: true,
      },
      petHealth: {
        typeof: 'boolean',
        required: true,
        is: {
          other: false,
        },
      },
      petBreedType: {
        typeof: 'string',
        required: pet.pet === 'dog',
      },
      petBreed: {
        typeof: 'string',
        required: pet.petBreedType !== 'Mixed',
      },
      crossBreedSize: {
        typeof: 'string',
        required: (pet.pet === 'dog' && pet.petBreedType !== 'Pedigree'),
      },
      petPurchasePrice: {
        typeof: 'number',
        required: true,
      },
      chippedTagged: {
        typeof: 'boolean',
        required: true,
      },
      petVaccinationUpToDate: {
        typeof: 'boolean',
        required: true,
      },
      neutered: {
        typeof: 'boolean',
        required: true,
      },
      professionalUse: {
        typeof: 'boolean',
        required: pet.pet === 'dog',
      },
      aggressivity: {
        typeof: 'boolean',
        required: pet.pet === 'dog',
      },
      aggressivAttack: {
        typeof: 'boolean',
        required: pet.pet === 'dog',
      },
      petPlaceAlcoholServed: {
        typeof: 'boolean',
        required: true,
      },
    };
  });
};

export const createPetRules = (data, config) => {
  const validationHelpers = {};

  validationHelpers.config = config;

  return {
    getProposerRules: initProposerRules(validationHelpers),
    getCoverRules: initCoverRules(validationHelpers),
  };
};

export const prevalidate = async (dataRaw, config) => {
  const data = cloneDeep(dataRaw);
  const rules = createPetRules(data, config);

  await Promise.all(
    ['cover', 'proposer'].map(async stepId => {
      if (!data[stepId] || !isObjectLike(data[stepId])) return;

      const stepTemplate = stepId === 'cover' ? cloneDeep(pet) : getQuotePayload(stepId.toUpperCase());
      const stepFieldsSorted = Object.keys(stepTemplate).sort();
      const stepRules = rules[`get${capitalize(stepId)}Rules`](data[stepId], data);
      let stepValidity = null;

      if (stepId === 'cover') {
        stepValidity = await Promise.all((data[stepId].pets || []).map(async (pet, petIndex) => await Promise.all(
          stepFieldsSorted.map(field => {
            return validate(pet[field], stepRules[petIndex][field]);
          }),
        )));
        (data[stepId].pets || []).forEach((singlePet, petIndex) => {
          stepFieldsSorted
            .filter((item, index) => !stepValidity[petIndex][index].valid)
            .forEach((field) => {
              singlePet[field] = stepTemplate[field];
            });
        });
      } else {
        stepValidity = await Promise.all(stepFieldsSorted.map(field => {
          return validate(data[stepId][field], stepRules[field]);
        }));

        stepFieldsSorted
          .filter((item, index) => !stepValidity[index].valid)
          .forEach((field) => {
            data[stepId][field] = stepTemplate[field];
          });
      }
    }),
  );

  return data;
};
